/**
 * Renata Especialistas Dashboard — FASE 9
 * /app/renata/agents
 * Muestra qué agentes participaron, sus dominios y estadísticas de uso del tenant.
 */
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { Bot, RefreshCw, Activity, Zap, Clock, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import type { AgentRun } from "@/lib/renata/agents/types";

export const Route = createFileRoute("/app/renata/agents")({
  component: RenataAgentsPage,
  head: () => ({ meta: [{ title: "Especialistas — Renata — Structura Market" }] }),
});

interface AgentInfo {
  id: string;
  name: string;
  description: string;
  domain: string;
  skillIds: string[];
  priority: number;
}

interface AgentStats {
  total_runs: number;
  total_cost_microcents: number;
  total_errors: number;
  last_run_at: string | null;
}

const DOMAIN_COLORS: Record<string, string> = {
  inventario: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  ventas: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  marketing: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  catálogo: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  operaciones: "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300",
  compras: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  soporte: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  clientes: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  analytics: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  finanzas: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
};

function AgentCard({ agent }: { agent: AgentInfo }) {
  const domainClass = DOMAIN_COLORS[agent.domain] ?? "bg-muted text-muted-foreground";
  return (
    <div className="rounded-xl border bg-card p-4 space-y-2 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
            <Bot className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <p className="text-sm font-medium">{agent.name}</p>
            <Badge variant="outline" className={cn("text-[10px] py-0 mt-0.5", domainClass)}>
              {agent.domain}
            </Badge>
          </div>
        </div>
        <span className="sr-only">prioridad {agent.priority}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{agent.description}</p>
      <p className="text-[11px] text-muted-foreground">
        Ayuda a Renata a resolver problemas de {agent.domain}.
      </p>
    </div>
  );
}

function RunRow({ run }: { run: AgentRun }) {
  const agents = Array.isArray(run.agent_ids) ? run.agent_ids : [];
  const hasError = Boolean(run.error);
  return (
    <div className={cn(
      "flex items-center gap-3 rounded-lg border px-3 py-2 text-xs",
      hasError ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20" : "bg-muted/20",
    )}>
      {hasError ? (
        <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
      ) : (
        <Activity className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1">
          {agents.map((id) => (
            <Badge key={id} variant="outline" className="text-[10px] py-0">{id}</Badge>
          ))}
        </div>
        {hasError && <p className="mt-0.5 text-red-600 dark:text-red-400 truncate">{run.error}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {run.duration_ms}ms
        </span>
        <Badge variant="outline" className="text-[10px] py-0">{run.mode}</Badge>
        <span>{new Date(run.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
      </div>
    </div>
  );
}

function RenataAgentsPage() {
  const tenantId = useWorkspaceTenantId();
  const [agents, setEspecialistas] = useState<AgentInfo[]>([]);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [recentRuns, setRecentRuns] = useState<AgentRun[]>([]);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(
    () => clientRequestHeaders(tenantId ?? ""),
    [tenantId],
  );

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await fetch("/api/renata/agents", {
        credentials: "include",
        headers: headers(),
      });
      const data = (await res.json()) as {
        agents: AgentInfo[];
        stats: AgentStats | null;
        recentRuns: AgentRun[];
      };
      setEspecialistas(data.agents ?? []);
      setStats(data.stats ?? null);
      setRecentRuns(data.recentRuns ?? []);
    } finally {
      setLoading(false);
    }
  }, [tenantId, headers]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!tenantId) return null;

  return (
    <div className="flex min-w-0 flex-col p-4 md:p-6 space-y-4">
      <RenataPageChrome
        screenId="agents"
        title="Especialistas de Renata"
        subtitle="Cada especialista ayuda a Renata a resolver un tipo de problema. Vos hablás con Renata; ellos trabajan en segundo plano."
        showBack
        breadcrumb={["Equipo", "Especialistas"]}
      >
        <div className="flex justify-end">
          <Button size="sm" variant="outline" className="gap-2" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            Actualizar
          </Button>
        </div>
        {stats && (
          <div className="flex flex-wrap gap-4">
            {[
              { label: "Consultas", value: stats.total_runs },
              { label: "Con error", value: stats.total_errors },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-lg font-bold tabular-nums">{s.value}</div>
                <div className="text-[10px] text-muted-foreground">{s.label}</div>
              </div>
            ))}
            {stats.last_run_at && (
              <div className="text-center">
                <div className="text-xs font-medium">
                  {new Date(stats.last_run_at).toLocaleString("es-AR")}
                </div>
                <div className="text-[10px] text-muted-foreground">Última actividad</div>
              </div>
            )}
          </div>
        )}
      </RenataPageChrome>

      <div className="min-h-0 flex-1 space-y-6">
        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Agent grid */}
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                Especialistas disponibles ({agents.length})
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {agents.map((a) => (
                  <AgentCard key={a.id} agent={a} />
                ))}
              </div>
            </div>

            {/* Recent runs */}
            {recentRuns.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                  <Zap className="inline h-3 w-3 mr-1" />
                  Actividad reciente ({recentRuns.length})
                </p>
                <div className="space-y-1.5">
                  {recentRuns.slice(0, 15).map((run) => (
                    <RunRow key={run.id} run={run} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
